import { useState } from "react";
import { Reveal } from "@/components/Dividers";
import { SkillsBoard } from "@/components/SkillsBoard";
import { ProjectModal } from "@/components/ProjectModal";
import { Microscope, BrainCircuit, MousePointerClick } from "lucide-react";

const projects = {
  biomech: {
    id: "biomech",
    kicker: "Sample 01 — Biomechanics",
    title: "Biomechanics Project",
    image: "/media/biomech.jpg",
    summary: "A compression garment for hypertrophic scarring, engineered to move with the elbow through every degree of flexion, not against it.",
    sections: [
      { heading: "The problem", body: "Standard compression garments fight the joint. At the elbow they bunch, slip, and lose therapeutic pressure exactly where scar tissue needs it most." },
      { heading: "The approach", body: "Zoned compression mapped to elbow kinematics: high-stretch panels across the crease, anchored pressure over the scar field, and seam paths that follow the natural flexion lines." },
      { heading: "Status", body: "Currently in build. Prototyping, pressure-mapping and wear-testing notes will be published here as the garment evolves." },
    ],
  },
  past: {
    id: "past",
    kicker: "Sample 02 — Archive",
    title: "Past Projects",
    image: "https://images.pexels.com/photos/5827287/pexels-photo-5827287.jpeg?auto=compress&cs=tinysrgb&w=1600",
    summary: "A rolling archive of what I've built as a biomedical engineer, from MDPP partner briefs to bench prototypes and coursework that escaped the classroom.",
    sections: [
      { heading: "Project 01", body: "Coming soon. A short title, the outcome in one line, and the part of the build I owned." },
      { heading: "Project 02", body: "Coming soon. A short title, the outcome in one line, and the part of the build I owned." },
      { heading: "Project 03", body: "Coming soon. A short title, the outcome in one line, and the part of the build I owned." },
    ],
  },
};

const Hint = () => (
  <span className="absolute top-6 right-6 flex items-center gap-2 rounded-full border border-white/20 bg-black/40 backdrop-blur px-3 py-1.5 font-tech text-[9px] tracking-[0.3em] uppercase text-white/70 transition-colors duration-300 group-hover:border-[#FF1493] group-hover:text-[#FF66B2]">
    <MousePointerClick size={11} strokeWidth={1.5} /> Open
  </span>
);

const ProjectTile = ({ project, onOpen, testId, icon: Icon, label, title, blurb, minH, overlay, alt, extra }) => (
  <button
    type="button"
    onClick={() => onOpen(project)}
    className={`tile-glow group relative w-full h-full ${minH} rounded-3xl border border-white/10 overflow-hidden text-left cursor-pointer`}
    data-testid={testId}
  >
    <img src={project.image} alt={alt} className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
    <div className={`absolute inset-0 ${overlay}`} />
    {extra}
    <Hint />
    <div className="absolute bottom-0 p-8 md:p-10 max-w-lg">
      <p className="font-tech text-[10px] tracking-[0.35em] uppercase text-[#FF66B2] flex items-center gap-2">
        <Icon size={13} strokeWidth={1.5} /> {label}
      </p>
      <h3 className="font-display italic text-3xl md:text-4xl mt-3">{title}</h3>
      <p className="text-white/60 text-sm md:text-base mt-3 leading-relaxed">{blurb}</p>
    </div>
  </button>
);

export const Projects = () => {
  const [active, setActive] = useState(null);

  return (
    <section id="projects" className="relative py-28 md:py-40" data-testid="projects-section">
      <div className="relative max-w-7xl mx-auto px-6 md:px-10">
        <Reveal>
          <p className="font-tech text-[11px] tracking-[0.4em] uppercase text-[#FF66B2]">Onboard Emergency</p>
          <h2 className="font-display font-black text-4xl md:text-5xl lg:text-6xl tracking-tight mt-6">
            Projects &amp; <span className="italic text-[#FF1493]">lab notes.</span>
          </h2>
        </Reveal>

        <div className="mt-16 flex flex-col gap-6">
          <Reveal>
            <ProjectTile
              project={projects.biomech}
              onOpen={setActive}
              testId="project-tile-imaging"
              icon={Microscope}
              label="Sample 01"
              title="Biomechanics Project"
              blurb="Currently building a compression garment for hypertrophic scarring — engineered to move with the elbow through every degree of flexion, not against it."
              minH="min-h-[380px] md:min-h-[440px]"
              overlay="bg-gradient-to-t from-black via-black/30 to-transparent"
              alt="Compression sleeve on a flexed elbow joint with pink rim lighting"
              extra={<div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,_rgba(255,20,147,0.25),_transparent_60%)] mix-blend-screen" />}
            />
          </Reveal>

          <Reveal delay={0.1}>
            <ProjectTile
              project={projects.past}
              onOpen={setActive}
              testId="project-tile-microfluidics"
              icon={BrainCircuit}
              label="Sample 02"
              title="Past Projects"
              blurb="The archive — MDPP partner briefs, bench prototypes, and coursework that escaped the classroom."
              minH="min-h-[260px]"
              overlay="bg-gradient-to-r from-black via-black/50 to-transparent"
              alt="Laboratory containers from above"
            />
          </Reveal>

          <Reveal delay={0.15}>
            <SkillsBoard />
          </Reveal>
        </div>
      </div>

      <ProjectModal open={!!active} onOpenChange={(o) => !o && setActive(null)} project={active} />
    </section>
  );
};
